import { AlertCircle, CheckCircle2, Lock } from "lucide-react"
import { PageHeader } from "@/app/components/PageHeader"
import type { ClientTheme } from "@/app/lib/themes"

// ── Shared presentational helpers for write pages ────────────────────────────

export function inputStyle(theme: ClientTheme): React.CSSProperties {
  return {
    width: "100%",
    padding: "8px 11px",
    borderRadius: "8px",
    backgroundColor: theme.bgSurface2,
    border: `1px solid ${theme.borderColor}`,
    color: theme.textPrimary,
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    outline: "none",
  }
}

export function labelStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "11px",
    fontWeight: 600,
    color: theme.textDim,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  }
}

export function Field({
  label,
  required,
  theme,
  children,
}: {
  label: string
  required?: boolean
  theme: ClientTheme
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span style={labelStyle(theme)}>
        {label}
        {required && <span style={{ color: theme.brandAccent }}> *</span>}
      </span>
      {children}
    </label>
  )
}

export function Input({
  value,
  onChange,
  type = "text",
  placeholder,
  step,
  theme,
}: {
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  step?: string
  theme: ClientTheme
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      step={step}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle(theme)}
    />
  )
}

export function ErrorBanner({ theme, message }: { theme: ClientTheme; message: string }) {
  return (
    <div
      className="mt-4 rounded-lg p-3 flex items-center gap-2"
      style={{
        backgroundColor: `${theme.brandAccent}12`,
        border: `1px solid ${theme.brandAccent}40`,
      }}
    >
      <AlertCircle size={15} style={{ color: theme.brandAccent }} />
      <span style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.brandAccent }}>
        {message}
      </span>
    </div>
  )
}

export function SuccessBanner({ theme, message }: { theme: ClientTheme; message: string }) {
  return (
    <div
      className="mt-4 mb-2 rounded-lg p-3 flex items-center gap-2"
      style={{ backgroundColor: `${theme.brandTeal}12`, border: `1px solid ${theme.brandTeal}40` }}
    >
      <CheckCircle2 size={15} style={{ color: theme.brandTeal }} />
      <span style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textPrimary }}>
        {message}
      </span>
    </div>
  )
}

export function ReadOnlyCard({
  theme,
  title,
  subtitle,
  icon,
}: {
  theme: ClientTheme
  title: string
  subtitle: string
  icon?: React.ReactNode
}) {
  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="max-w-[1100px]">
        <PageHeader title={title} subtitle={subtitle} theme={theme} icon={icon} />
        <div
          className="rounded-2xl p-8 flex items-center gap-3"
          style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
        >
          <Lock size={18} style={{ color: theme.textDim }} />
          <div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "14px",
                fontWeight: 600,
                color: theme.textPrimary,
              }}
            >
              Read-only access
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "13px",
                color: theme.textMuted,
                marginTop: "2px",
              }}
            >
              Your role does not permit managing this data. Contact an owner or admin.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function formatApiError(error: unknown): string {
  if (!(error instanceof Error)) return "Something went wrong."
  try {
    const parsed = JSON.parse(error.message)
    if (Array.isArray(parsed?.errors)) return parsed.errors.join(", ")
    if (typeof parsed?.error === "string") return parsed.error
  } catch {
    // message was not JSON
  }
  return error.message || "Something went wrong."
}
